import { memo } from "react";
import { useNavigate } from "react-router-dom";
import type { TreeNodeData } from "../../api/trees";

export type { TreeNodeData };

interface TreeNodeProps {
  id: string;
  treeId: number;
  data: TreeNodeData;
  isRoot: boolean;
  style: React.CSSProperties;
}

/**
 * Custom node renderer for the family tree visualization.
 * Displays person's name, dates, and provides click navigation.
 */
function TreeNodeComponent({ id, treeId, data, isRoot, style }: TreeNodeProps) {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/trees/${treeId}/persons/${id}`);
  };

  // Determine background color based on sex
  const bgColor =
    data.sex === "M"
      ? "bg-blue-100 border-blue-300 hover:bg-blue-200"
      : data.sex === "F"
        ? "bg-pink-100 border-pink-300 hover:bg-pink-200"
        : "bg-vault-100 border-vault-300 hover:bg-vault-200";

  // Format life dates
  const lifeDates = [];
  if (data.birthDate) lifeDates.push(`b. ${data.birthDate}`);
  if (data.deathDate) lifeDates.push(`d. ${data.deathDate}`);
  const lifeSpan = lifeDates.join(" • ");

  return (
    <div
      className={`absolute cursor-pointer rounded-lg border-2 p-2 shadow-sm transition-all ${bgColor} ${
        isRoot ? "ring-2 ring-vault-500 ring-offset-2" : ""
      }`}
      style={style}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          handleClick();
        }
      }}
      title={`Click to view ${data.name}`}
    >
      {/* Avatar initial */}
      <div className="flex items-center gap-2">
        <div
          className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold text-white ${
            data.sex === "M"
              ? "bg-blue-500"
              : data.sex === "F"
                ? "bg-pink-500"
                : "bg-vault-400"
          }`}
        >
          {data.name.charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1 overflow-hidden">
          <p className="truncate text-sm font-semibold text-vault-900">{data.name}</p>
          {lifeSpan && <p className="truncate text-xs text-vault-500">{lifeSpan}</p>}
        </div>
      </div>
    </div>
  );
}

export default memo(TreeNodeComponent);
